import { Checkbox } from "@template/components";
import * as S from "./styles.ts";
import { useEffect, useState } from "react";
import { styled } from "styled-components";
import { CodeBlock } from "./CodeBlock";
import InfoPopup from "./InfoPopup";
import CheckCircle from "@template/icons/CheckCircle.tsx";
import ChevronUp from "@template/icons/ChevronUp.tsx";

const CommandCategories = ({
                             commandsDictionary,
                             category,
                             setCategory,
                             chargeable,
                             setChargeable,
                           }) => {
  return (
    <S.CategoriesContainer>
      <S.CategoryButtonContainer>
        <S.CategoryButton
          $active={category === ""}
          onClick={() => setCategory("")}
        >
          All
        </S.CategoryButton>
        <S.CategoryButton
          $active={category === "get"}
          disabled={
            commandsDictionary.get.length === 0 ||
            (!commandsDictionary.get.some((c) => c.chargeable) && chargeable)
          }
          onClick={() => setCategory("get")}
        >
          GET
        </S.CategoryButton>
        <S.CategoryButton
          $active={category === "post"}
          disabled={
            commandsDictionary.post.length === 0 ||
            (!commandsDictionary.post.some((c) => c.chargeable) && chargeable)
          }
          onClick={() => setCategory("post")}
        >
          POST
        </S.CategoryButton>
        <S.CategoryButton
          $active={category === "put"}
          disabled={
            commandsDictionary.put.length === 0 ||
            (!commandsDictionary.put.some((c) => c.chargeable) && chargeable)
          }
          onClick={() => setCategory("put")}
        >
          PUT
        </S.CategoryButton>
        <S.CategoryButton
          $active={category === "delete"}
          disabled={
            commandsDictionary.delete.length === 0 ||
            (!commandsDictionary.delete.some((c) => c.chargeable) && chargeable)
          }
          onClick={() => setCategory("delete")}
        >
          DELETE
        </S.CategoryButton>
        <S.CategoryButton
          $active={chargeable}
          disabled={
            !(
              (!category && commandsDictionary.chargeable.length > 0) ||
              commandsDictionary.chargeable.some(
                (c) => c.method.toLowerCase() === category,
              )
            )
          }
          onClick={() => setChargeable(!chargeable)}
        >
          <Checkbox
            checked={chargeable}
            readOnly
          />
          <p>*CHARGEABLE</p>
        </S.CategoryButton>
      </S.CategoryButtonContainer>
      <S.CategoryDisclaimer>
        *You will be charged every time this command is run
      </S.CategoryDisclaimer>
    </S.CategoriesContainer>
  );
};

interface TableBlockProps {
  title: string;
  customBody?: any;
}

export const BlockContainer = styled.div`
  width: 100%;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border: 1px solid ${(props) => props.theme.colors.base}10;
  font-size: 14px;
  overflow: hidden;
`;

export const BlockTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.default[500]};
`;

export const BlockTableHeaderContainer = styled.thead`
  background: ${(props) => props.theme.colors.base};
`;

export const BlockTableBodyContainer = styled.tbody``;

export const BlockTable = styled.table`
  text-align: left;
  width: 100%;
`;

export const BlockTableRow = styled.tr`
  padding: 12px 24px;
  display: grid;
  grid-template-columns: minmax(0, 45%) minmax(0, 35%) minmax(0, 20%);

  &:not(:first-of-type) {
    border-top: 1px solid ${(props) => props.theme.colors.base}10;
  }
`;

export const BlockTableHeader = styled.th`
  color: #9ea0aa;
  font-size: 12px;
  line-height: 14px;
  font-weight: 600;

  &:last-of-type {
    text-align: right;
  }
`;

export const BlockTableCell = styled.td`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  overflow-y: clip;
  overflow-x: auto;

  &:last-of-type {
    text-align: right;
    justify-content: end;
  }

  &[data-is-required="true"] {
    span svg path {
      fill: ${(props) => props.theme.colors.primary};
      stroke: ${(props) => props.theme.colors.primary};
    }
  }

  &[data-is-required="false"] {
    span {
      opacity: 0.2;
    }
  }
`;

const TableBlock = ({ title, customBody }: TableBlockProps) => {
  return (
    <BlockContainer>
      <BlockTitle>{title}</BlockTitle>
      {customBody && (
        <BlockTable>
          <BlockTableHeaderContainer>
            <BlockTableRow>
              <BlockTableHeader>Name</BlockTableHeader>
              <BlockTableHeader>Type</BlockTableHeader>
              <BlockTableHeader>Required</BlockTableHeader>
            </BlockTableRow>
          </BlockTableHeaderContainer>
          <BlockTableBodyContainer>{customBody}</BlockTableBodyContainer>
        </BlockTable>
      )}
    </BlockContainer>
  );
};

const Request = ({ parameters, body, type }) => {
  const { example } = body;
  const { variations } = body;

  const queryDetails = [];
  const bodyDetails = [];
  const variationDetails = [];

  if (body.length === 0 && parameters.length === 0) {
    return (
      <TableBlock title="This command has no URL/Query String parameters" />
    );
  }

  if (variations?.length > 0) {
    variations.map((v, index) => {
      variationDetails.push(
        <CodeBlock
          key={index + v.type}
          title={v.name}
          response={v.example}
          light
          description={v.description}
        />
      );
    });
  }

  const javaType = (input: string): string => {
    const parts = input.split(".");
    return parts[parts.length - 1];
  };

  function createStyledTable(dataArray, arrayToPushTo) {
    dataArray.map((i, index) => {
      arrayToPushTo.push(
        <BlockTableRow key={index + i.type}>
          <BlockTableCell>
            {i.name}
            {i.description && <InfoPopup info={i.description} />}
          </BlockTableCell>
          <BlockTableCell>{javaType(i.type)}</BlockTableCell>
          <BlockTableCell data-is-required={i.required}>
            <CheckCircle color="#ea1b4b" />
          </BlockTableCell>
        </BlockTableRow>
      );
    });
  }

  if (body.fields?.length > 0) {
    createStyledTable(body.fields, bodyDetails);
  }
  if (parameters.length > 0) {
    createStyledTable(parameters, queryDetails);
  }

  return (
    <>
      {body.fields?.length > 0 && (
        <TableBlock
          title={`${type.toUpperCase()} Body`}
          customBody={bodyDetails}
        />
      )}
      {parameters.length > 0 && (
        <TableBlock
          title={`${type.toUpperCase()} Parameters`}
          customBody={queryDetails}
        />
      )}
      {body.variations?.length > 0 && variationDetails}
      {example && (
        <CodeBlock
          title="Example"
          response={example}
          light
        />
      )}
    </>
  );
};

const Command = ({ command }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <S.CommandContainer>
      <S.CommandInfo
        onClick={() => setShowDetails(!showDetails)}
        tabIndex={0}
      >
        <S.CommandTitle type={command.method}>
          {command.endpoint}
          {command.chargeable && (
            <S.ChargeableBanner>
              <p>Chargeable</p>
              <i className="icon-pound" />
            </S.ChargeableBanner>
          )}
          <S.ExpandArrowContainer $expanded={showDetails}>
            <ChevronUp />
          </S.ExpandArrowContainer>
        </S.CommandTitle>
        <S.CommandDescription>{command.description}</S.CommandDescription>
      </S.CommandInfo>
      {showDetails && (
        <>
          <S.DetailsContainer>
            <S.DetailsTitle>Request Details</S.DetailsTitle>
            <Request
              parameters={command.parameters}
              body={command.body || []}
              type={command.method}
            />
          </S.DetailsContainer>
          <S.DetailsContainer>
            <S.DetailsTitle>Response Details</S.DetailsTitle>
            {command.responses.map((res, i) => (
              <CodeBlock
                key={i}
                code={res.status_code}
                response={res.result_example}
                isCollection={res.is_collection}
              />
            ))}
          </S.DetailsContainer>
        </>
      )}
    </S.CommandContainer>
  );
};

const CommandList = ({ commands }) => {
  return (
    <S.CommandListContainer>
      {commands.map((c, i) => (
        <Command
          key={c.method + c.endpoint + i}
          command={c}
        />
      ))}
    </S.CommandListContainer>
  );
};

export const Commands = ({ commands }) => {
  const [category, setCategory] = useState("");
  const [chargeable, setChargeable] = useState(false);
  const [filteredCommands, setFilteredCommands] = useState([]);

  useEffect(() => {
    setCategory("");
    setChargeable(false);
  }, [commands]);

  let commandsDictionary = {
    get: [],
    post: [],
    put: [],
    delete: [],
    chargeable: [],
  };
  commands.forEach((command) =>
    commandsDictionary[command.method.toLowerCase()]?.push(command)
  );
  commands.forEach(
    (command) =>
      command.chargeable && commandsDictionary.chargeable?.push(command)
  );

  useEffect(() => {
    let newCommands;
    if (category === "") {
      // Just so we can order them by type...
      newCommands = [
        ...commandsDictionary.get,
        ...commandsDictionary.post,
        ...commandsDictionary.put,
        ...commandsDictionary.delete,
      ];
    } else {
      newCommands = commandsDictionary[category];
    }

    if (chargeable) {
      newCommands = newCommands.filter((c) => c.chargeable);
    }

    setFilteredCommands(newCommands);
  }, [category, chargeable, commands]);

  return (
    <S.CommandsContainer>
      <CommandCategories
        category={category}
        setCategory={setCategory}
        commandsDictionary={commandsDictionary}
        chargeable={chargeable}
        setChargeable={setChargeable}
      />
      <CommandList commands={filteredCommands} />
    </S.CommandsContainer>
  );
};
